import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router-dom";

import App from "./App.tsx";
import { initSentry } from "./sentry/init.ts";

import "./assets/styles/global.css";

import { StorageListener } from "@/components/StorageListener";
import ThemeProvider from "@/components/ThemeProvider";
import { AppIntlProvider } from "@/i18n/AppIntlProvider";
import { store } from "@/redux/store";
import { sanitizeBrowserQueryParams } from "@/utils/form/queryParams";

/**
 * Removes internal query state before the router reads the initial location.
 *
 * @returns {void}
 */
const sanitizeInitialBrowserQueryParams = (): void => {
  const currentQueryString = window.location.search.replace(/^\?/, "");
  const sanitizedQueryString = sanitizeBrowserQueryParams(
    new URLSearchParams(window.location.search),
  ).toString();

  if (sanitizedQueryString === currentQueryString) {
    return;
  }

  const nextUrl = `${window.location.pathname}${
    sanitizedQueryString ? `?${sanitizedQueryString}` : ""
  }${window.location.hash}`;

  window.history.replaceState(window.history.state, "", nextUrl);
};

sanitizeInitialBrowserQueryParams();
initSentry();

const router = createBrowserRouter([
  {
    path: "/*",
    element: <App />,
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppIntlProvider>
      <Provider store={store}>
        <StorageListener>
          <ThemeProvider defaultTheme='light' storageKey='vite-ui-theme'>
            <RouterProvider
              router={router}
              future={{
                v7_startTransition: true,
              }}
            />
          </ThemeProvider>
        </StorageListener>
      </Provider>
    </AppIntlProvider>
  </React.StrictMode>,
);
